import { t, Trans } from '@lingui/macro';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import { MAX_PASSKEY_LENGTH } from '@helpers/passkey';

import { convertToServerFormat } from '../../helpers/dateUtils';

import { PrivacySettingsColors } from './colors';
import PasskeyCopy from './PasskeyCopy';
import { PasskeyInputProps } from './types';

// ===== STYLED COMPONENTS =====

const PasskeyInputSection = styled.div`
  margin-bottom: 10px;
  label {
    font-size: 14px;
    font-weight: bold;
    display: block;
    margin: 10px 0 5px 0 !important;
  }
`;

const PasskeyInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid ${PrivacySettingsColors.BorderGray};
  border-radius: 4px;
  background: ${PrivacySettingsColors.White};
  max-width: 300px;

  && input[type="text"] {
    border: none;
    outline: none;
    padding: 8px 12px;
    background: transparent;
    border-radius: 4px;
    &:hover,
    &:focus {
      border: none;
      outline: none;
    }
  }
`;

const GenerateButton = styled.button`
  position: absolute;
  right: 35px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;

  &:hover {
    background: ${PrivacySettingsColors.LightGrayBackground};
  }

  &:active {
    background: ${PrivacySettingsColors.DisabledBackground};
  }

  .bff-refresh {
    font-size: 14px;
    color: ${PrivacySettingsColors.DisabledText};
  }
`;

const CopyButtonWrapper = styled.div`
  position: absolute;
  right: 15px;
  top: 15px;
`;

const InputInfo = styled.div`
  font-size: 12px;
  color: ${PrivacySettingsColors.SecondaryText};
  margin: 5px 0 0 0;
`;

// ===== MAIN COMPONENT =====

/**
 * PasskeyInput Component
 * Handles passkey input field with generation and copy functionality
 * Shows system-generated passkey with regenerate and copy options
 */
const PasskeyInput: React.FunctionComponent<PasskeyInputProps> = ({
  contextType,
  passkey,
  onGeneratePasskey,
  onPasskeyChange,
  expiryDate
}) => {
  // Initialize state with empty string instead of passkey prop
  const [inputVal, setInputVal] = useState('');

  // Only update inputVal when passkey prop changes AND it's not empty
  // This ensures we only update when the generate button is clicked
  useEffect(() => {
    if (passkey) {
      setInputVal(passkey);
    }
  }, [passkey]);

  function handlePasswordChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const { value } = event.target;

    // Restrict passkey length to maximum characters
    if (value.length > MAX_PASSKEY_LENGTH) {
      return;
    }

    setInputVal(value);

    // Notify parent component about the change
    if (onPasskeyChange) {
      onPasskeyChange(value);
    }
  }
  return (
    <PasskeyInputSection>
      <label className="bf-label bf-label--primary">
        <Trans>New Passkey</Trans>
      </label>
      <PasskeyInputContainer>
        <input
          className="j-nofocus j-simple-password-change bf-input"
          id={`${contextType}_password`}
          maxLength={MAX_PASSKEY_LENGTH}
          name={`${contextType}[encrypted_passkey]`}
          onChange={handlePasswordChange}
          placeholder="New Passkey"
          type="text"
          value={inputVal}
        />
        <GenerateButton
          onClick={onGeneratePasskey}
          title={t`Generate new passkey`}
          type="button"
        >
          <span className="bff-refresh" />
        </GenerateButton>
        {/* Only show copy button when there's a passkey to copy */}
        {passkey && (
          <CopyButtonWrapper>
            <PasskeyCopy passkey={passkey} />
          </CopyButtonWrapper>
        )}
      </PasskeyInputContainer>
      <InputInfo>
        <Trans>Enter your own passkey or click the refresh icon to generate one.</Trans>
      </InputInfo>
      <input
        name={`${contextType}[passkey_expires_at]`}
        type="hidden"
        value={convertToServerFormat(expiryDate)}
      />
    </PasskeyInputSection>
  );
};

export default PasskeyInput;
